<template>
  <div class="gameCategary" :data-cat="categary.id" ref="target">
    <div class="title" v-if="visible">
      <div class="flex-center-y">
        <Image
          :src="categary.customIcon || categary.defaultIcon || categary.icon"
          class="size-6 mr-2"
        />
        <span class="text-13 font-semibold">{{ categary.customName || categary.name }}</span>
      </div>
      <div class="flex-center-x text-11 py-2.5 gap-1 [&>span]:px-2 [&>span]:rounded-[3px] [&>span]:bg-[var(--theme-gray-green70)] [&>span]:flex-center">
        <span @click="swipe.prev" class="!bg-[var(--theme-gray-green40)]"> <van-icon name="arrow-left" size="12" color="#ffffff" /> </span>
        <span class="font-[600]" v-if="total >= 0"> Todos ({{ total }}) </span>
        <span class="font-[600] !px-6" v-else> <van-loading type="spinner" size="12" color="#ffffff" /> </span>
        <span @click="swipe.next" class="!bg-[var(--theme-gray-green40)]"> <van-icon name="arrow" size="12" color="#ffffff" /> </span>
        <span @click="gotoGameCategory(categary, $router)" class="mx-0.5">{{ $t('Common.more') }}</span>
      </div>
    </div>
    <div class="p-[12px_16px] min-h-[130px] bg-[#252E2D] relative rounded-b-8 overflow-hidden">
      <template v-if="tabs.length">
        <!-- <span class="arrow rotate-180 left-0" v-if="showLeft"></span> -->
        <!-- <span class="arrow right-0" v-if="showRight"></span> -->
        <van-swipe
          ref="swipe"
          lazy-render
          :loop="false"
          :show-indicators="false"
          @change="onSwipeChange"
          @drag-start="preloadNextPage"
        >
          <van-swipe-item v-for="(tab, index) in tabs" :key="index">
            <GameLists
              v-if="tab"
              :placeholderCount="pageSize"
              :lazy-load="true"
              :list="tab"
              itemClass="wh-[76px_105px]"
              class="gap-3"
              :onClickItem="gotoGame"
            />
          </van-swipe-item>
          <van-swipe-item v-if="hasMore" class="loadingPage" :key="tabs.length">
            <GameLists
              :placeholderCount="pageSize"
              :list="[]"
              itemClass="wh-[76px_105px]"
              class="gap-3"
              :onClickItem="gotoGame"
            />
          </van-swipe-item>
        </van-swipe>
      </template>
      <Nodata v-else-if="total == 0" />
      <GameLists
        v-else
        :placeholderCount="pageSize"
        :list="[]"
        itemClass="wh-[76px_105px]"
        class="gap-3"
        :onClickItem="gotoGame"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { iGameCategory, iGame } from "@/types/index";
import { useUserInfo } from "@/store";
import { gotoGameCategory } from "@/utils";
import GameLists from "@a/components/GameLists/index.vue";
import { getGameListByCategory } from "@/api";
import useLazyload from "@/hooks/useLazyload";
import debounce from 'lodash-es/debounce'
import Nodata from '@/components/NoData/index.vue'

const { categary } = defineProps<{
  categary: iGameCategory;
}>();

const tabs = ref<iGame[][]>([]);
const total = ref(-1);
const curPage = ref(-1);
const pageSize = Math.ceil(categary.count/4) * 4;
const swipe = useTemplateRef<any>('swipe')
const hasMore = computed(() => total.value > (curPage.value + 1) * pageSize);

const onSwipeChange = (i) => {
  curPage.value = i;
};

const cacheKey= `gameCategory_${categary.id}_fp`

const saveCache = (key: string, data) => {
  localStorage.setItem(key, JSON.stringify({
    status: -2,
    gamelist: data,
    total: total.value,
  }))
}

const loadPage = async (page) => {
  if (tabs.value[page]?.length) return;
  
  const size = page == 0 ? Math.max(24, pageSize) : pageSize;

  try {
    const { code, data } = await getGameListByCategory({
      name: categary.name,
      pageIndex: page,
      pageSize: size,
      show: 1,
      scope: categary.type === 'Custom' ? "RoomGame" : "HomeGame",
    }).catch(e => ({code: 0, data: []}))
    if (code === 1 && data) {
      if (data.data.length) {
        const list = data.data.map((item) => item.info)
        tabs.value[page] = list.slice(0, pageSize);
        total.value = data.total;

        if (page == 0) saveCache(cacheKey, list)
      }

    }
  } catch (error) {
    console.error(error)
  }
};

const preloadNextPage = debounce(() => {
  if (hasMore.value) {
    loadPage(curPage.value + 1);
  }
}, 300, {leading: true})

const gotoGame = (game: iGame, router) => {
  if (useUserInfo().isLoggedin) {
    router.push(`/game?gameCode=${game.code}`);
  } else {
    router.push({
      path: "/middlepage",
      query: {
        gid: game.id,
      },
      state: {
        game: {
          gameId: game.id,
          gameName: game.name,
          banner: game.customIcon || game.icon,
        },
        categary: toRaw(categary),
        recList: toRaw(tabs.value[0]),
      },
    });
  }
};

watch(curPage, preloadNextPage);

const target = useTemplateRef<HTMLDivElement>('target')
const visible = useLazyload(target)
watch(visible, (v) => {
  if (v) {
    curPage.value = 0
    loadPage(0)
    loadPage(1)
  }
})
</script>

<style lang="scss" scoped>
.gameCategary {
  @apply mb-4 contain-content;

  &:last-child {
    @apply mb-0;
  }

  .title {
    @apply flex-between h-11 px-4 bgs-[url('@a/views/Home/images/title_bg.png')];
  }

  .arrow {
    @apply w-4 h-full bgs-[url('@a/views/Home/images/i_arrow_r.png'),center,12px] block absolute top-0;
  }

  // .item {
  //   @apply float-left mr-[12px] w-[76px] h-[105px] rounded-[5px] relative overflow-hidden;

  //   &:nth-child(4n) {
  //     @apply mr-0
  //   }

  //   &:nth-child(n+5) {
  //     @apply mt-[12px]
  //   }
  // }

  :deep(.van-swipe__track) .loadingPage {
    display: flex;
    height: auto;
    align-items: center;
    justify-content: center;
  }
}
</style>
